import { defineStore } from 'pinia'
import { getProject } from '@/api/mlap/projects'

export const useMlapProjectStore = defineStore('mlapProject', {
  state: () => ({
    project: null,
    // Set by the styxie payload on a full page load; consumed by the first
    // `ensureProject` so the landing page doesn't refetch what it just got.
    hydrated: false
  }),
  getters: {
    projectId: (state) => state.project?.id,
    // Gates every territory surface: the Territories tab, the locations table
    // grouping, the map spend chip.
    territoryAttribution: (state) =>
      Boolean(state.project?.territoryAttribution ?? state.project?.territory_attribution),
    // Split-down of territory spend across member locations. Server-side it is conjunctive
    // with territoryAttribution, so it can never be on by itself.
    territoryAllocation: (state) =>
      Boolean(state.project?.territoryAllocation ?? state.project?.territory_allocation),
    // Shape of that split-down: 'separate' (estimated columns beside the measured ones) or
    // 'blended' (folded into each location's own numbers).
    territoryAllocationMode: (state) =>
      (state.project?.territoryAllocationMode ?? state.project?.territory_allocation_mode) === 'blended' ?
        'blended' : 'separate',
    ajaxPrefix: (state) => state.project ? `/mlap/ajax/projects/${state.project.id}` : '/mlap/ajax',
    pagePrefix: (state) => state.project ? `/mlap/projects/${state.project.id}` : '/mlap'
  },
  actions: {
    setProject(project) {
      this.project = project
    },
    hydrate(project) {
      this.setProject(project)
      this.hydrated = true
    },
    // Project layout `prepare`: awaited only when entering a project the store
    // doesn't hold — sibling page clicks within one project revalidate in the
    // background instead, keeping navigation instant while volatile fields
    // (location counts, territory flags) stay fresh.
    async ensureProject(id) {
      if (this.hydrated && this.project?.id === id) {
        this.hydrated = false
        return
      }

      if (this.project?.id !== id) {
        const res = await getProject(id)
        this.setProject(res.data)
        return
      }

      getProject(id)
        .then((res) => this.setProject(res.data))
        .catch(() => { /* keep the seeded project; a hard failure surfaces on the next full entry */ })
    }
  }
})
